/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Content Explorer
 *| (C) Copyright IBM Corp. 2017, 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'jquery',
	'../../lib/@waca/baglass/js/baglass/app/plugins/MenuActionInterface',
	'../../nls/StringResources',
	'../../lib/@waca/core-client/js/core-client/utils/Utils',
	'../../lib/@waca/core-client/js/core-client/utils/ContentFormatter',
	'../../lib/@waca/core-client/js/core-client/utils/BidiUtil'
], ($, MenuActionInterface, StringResources, Utils, ContentFormatter, BidiUtil) => {
	'use strict'; //NOSONAR: Meant to be strict

	const SWITCHER_VISIBLE_CLASS = 'switcherVisible';	// Hack... but other plugins need to know, so we drop this on <body/>
	function switcherVisibility(isVisible) {
		const $body = $('body');
		if ( isVisible ) {
			if (!$body.hasClass(SWITCHER_VISIBLE_CLASS)) {
				$body.addClass(SWITCHER_VISIBLE_CLASS);
			}
		} else {
			if ( $body.hasClass(SWITCHER_VISIBLE_CLASS)) {
				$body.removeClass(SWITCHER_VISIBLE_CLASS);
			}
		}
	}

	let _changeTitleClosure;
	$(window).on('resize', () => {
		_changeTitleClosure && _changeTitleClosure(true); //jshint ignore:line
	});

	class HomeActionController extends MenuActionInterface {

		onSelectItem(context) {
			const key = this._getPluginKey(context.target.itemId);
			const Glass = context.glassContext;
			switch (key) {
				case 'setHome':
					return Glass.currentAppView.setAsHome(context).then(() => {
						Glass.perspectiveLifeCycle.openPerspectiveController.setHomeFlag(Glass.currentAppView, true);
						Glass.showToast(StringResources.get('homeSet'));
					}, err => {
						Glass.showToast(StringResources.get('homeSetError'), { type: 'error' });
						if (this.logger) {
							this.logger.error(err);
						}
					});
				case 'myHome':
					return Glass.openAppView();
				default: {
					const items = context.target.plugin.itemSpec.items;
					const selectedAppView = items[context.target.specItemIndex].appView;
					return Glass.openAppView(selectedAppView.perspective, selectedAppView.context);
				}
			}
		}

		/**
		 * Called from the navbar home button
		 *
		 * @public
		 * @param {object} context-glass context
		 */
		onPress(context) {
			context.glassContext.openAppView();
		}

		onRemoveItem(context) {
			const items = context.target.plugin.itemSpec.items;
			const Glass = context.glassContext;
			const selectedAppView = items[context.target.specItemIndex].appView;
			const currentlySelected = selectedAppView === Glass.currentAppView;

			return new Promise.resolve().then(() => {
				if (currentlySelected) {
					return context.target.plugin.closeGlassMenu();
				}
			}).then(() => {
				return Glass.closeAppView(selectedAppView.perspective, selectedAppView.context.content.id || selectedAppView.context.id);
			}).then(() => {
				Glass.off('change:title', undefined, selectedAppView.viewId);
				this._manageTitle(Glass, context, Glass.currentAppView);
				return !currentlySelected;
			});
		}

		isItemVisible(context) {
			const key = this._getPluginKey(context.target.itemId);
			return key !== 'myHome';
		}

		onOpen(context) {
			const key = this._getPluginKey(context.target.itemId);
			const title = StringResources.get('homePage');
			if (key === 'viewSwitcher') {
				const switcherItems = context.target.plugin.itemSpec;
				const Glass = context.glassContext;
				const items = [];
				switcherItems.items = items;
				const featureId = this._getPluginFeature(context.target.itemId);
				this._manageTitle(Glass, context, context.target.plugin.appView);
				for (let i = 0; i < Glass.perspectiveLifeCycle.perspectiveSwitcherRegistry.size(); i++) {
					const appView = Glass.perspectiveLifeCycle.perspectiveSwitcherRegistry.getPerspectiveByIndex(i);
					const isR8Homepage = appView.isHomepage === true || appView.context && appView.context.content && appView.context.content.isHomepage;
					const isHomepage = appView.hasHomeFlag && appView.hasHomeFlag() || isR8Homepage;
					const isItemRemovable = !(isHomepage || (Glass.perspectiveSwitcherRegistry.size() === 1 && Glass.noCloseOnLastPerspective === true));
					const item = {
						id: context.target.itemId + i,
						featureId: featureId,
						appView: appView,
						removable: isItemRemovable,
						removeIconLabel: !isHomepage ? StringResources.get('switcherRemoveIconLabel') : undefined,
						rightIcon: isHomepage ? {
							icon: 'ba_portal-home_16',
							title: title
						} : '',
						label: appView.getTitle(),
						selected: appView === Glass.currentAppView,
						indent: switcherItems.indent
					};
					const icon = appView.getIcon();
					if (icon) {
						item.icon = icon;
						const iconColor = appView.getIconColor();
						if (iconColor) { //jshint ignore:line
							item.iconColor = `.${iconColor}`;
						}
					}
					if (isHomepage && !appView.isTransient()) {
						items.unshift(item); //Home is always as the top
					} else {
						items.push(item);
					}
				}
			}
		}

		onRender(context) {
			const appView = context.target.plugin.appView;
			const key = this._getPluginKey(context.target.itemId);
			if (key === 'viewSwitcher') {
				if (!appView.isTransient()) {
					context.glassContext.on('change:title', changeEvent => {
						this._changeTitle(context, changeEvent.value);
					}, context.target.plugin.appView.viewId);
				}
				const Glass = context.glassContext;
				this._manageTitle(Glass, context, appView);
				Glass.on('appView:loaded', evt => {
					this._manageTitle(Glass,context, evt.appView);
				});
			}
		}

		_manageTitle(Glass, context, appView) {
			const numOpenAssets = Glass.perspectiveLifeCycle.perspectiveSwitcherRegistry.size();
			const switcher = context.target.plugin || Glass.findPlugin(context.target.itemId);
			if (appView.isTransient()) {
				if (numOpenAssets > 0) {
					const itemOpenKey = numOpenAssets === 1 ? 'itemOpen' : 'itemsOpen';
					this._changeTitle(context, StringResources.get(itemOpenKey, {
						numOpenItems: numOpenAssets
					}), true);
					switcher.show();
					switcherVisibility(true);
				} else {
					switcher.hide();
					switcherVisibility(false);
				}
			} else {
				this._changeTitle(context, appView.getTitle());
				switcher.show();
				switcherVisibility(true);
			}
		}

		_changeTitle(context, newTitle, isTransient) {
			_changeTitleClosure = resize => {
				const Glass = context.glassContext;

				const perspectiveObj = Glass.currentPerspective;
				if (perspectiveObj && perspectiveObj.perspective === 'login') {
					return;
				}
				if (isTransient || !perspectiveObj || !perspectiveObj.isTransient()) {
					if (resize === true && !isTransient) {
						newTitle = Glass.currentAppView.getTitle();
					}
					const $ddMenuBtn = Glass.findPlugin('com.ibm.bi.glass.common.viewSwitcher').ddMenu.$el;
					const $tooltip = $ddMenuBtn.find('.bx--assistive-text');
					$tooltip.text(BidiUtil.enforceTextDirection(newTitle));
					$tooltip.attr('dir', BidiUtil.resolveBaseTextDir(newTitle));
					$ddMenuBtn.empty();
					if (perspectiveObj) {
						this._setSwitcherAssetIcon(perspectiveObj, $ddMenuBtn, newTitle);
					}
					const $switcherTitle = $(`<div class="switcherTitle"></div>`);
					$ddMenuBtn.append($switcherTitle.text(newTitle)).append($tooltip);
					Utils.setIcon($ddMenuBtn, 'common-titan-arrow-down', undefined, newTitle, true);
					ContentFormatter.middleShortenString($switcherTitle, true);
				} else {
					this._manageTitle(Glass, context, perspectiveObj);
				}
			};

			// Need a timeout when switching perspectives. The target perspective is hidden at this time.
			setTimeout(_changeTitleClosure, 1);
		}

		_setSwitcherAssetIcon(perspectiveObj, $ddMenuBtn, newTitle) {
			const isTransient = perspectiveObj.isTransient();
			const icon = isTransient ?  'ba_portal-stacked-scrolling--1' : perspectiveObj.getIcon();
			const color = perspectiveObj.getIconColor();
			Utils.setIcon($ddMenuBtn, icon, undefined, newTitle, false, color && `.${color}`);
		}

		/*
		 * 	get the featureId by removing the key from the itemId
		 * 	ie: The feature id of itemId "A.B.C.D" is "A.B.C"
		 */
		_getPluginFeature(itemId) {
			return itemId.replace(/\.([^.]+)$/, '');
		}

		_getPluginKey(itemId) {
			const idTokens = itemId.split('.');
			return idTokens[idTokens.length - 1];
		}
	}

	return HomeActionController;
});